import React, { useState } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";

const TITLE = "Calculatrice de difference entre deux dates";
const DESCRIPTION =
  "Calculez l'ecart exact entre deux dates en annees, mois, jours et heures. Tres utile pour contrats, anciennete, delais administratifs et planification.";
const KEYWORDS =
  "difference entre deux dates, calculatrice dates, calcul duree, jours entre dates, ecart calendrier";
const OG_URL = "https://do-calculate.com/calculator/fr/dategregoriandifferences/";

const GREGORIAN_MONTHS = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
];

const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Calculatrice de Différence Entre Deux Dates",
    description: DESCRIPTION,
    url: OG_URL,
    applicationCategory: "UtilityApplication",
    operatingSystem: "Any",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Accueil",
        item: "https://do-calculate.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Calculatrices",
        item: "https://do-calculate.com/fr/all-calculators/",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Calculatrice Différence Dates",
        item: OG_URL,
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Comment calculer la différence entre deux dates?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Pour calculer la différence entre deux dates, entrez la première date et la deuxième date dans les champs prévus, puis cliquez sur le bouton Calculer. Le résultat s'affichera en années, mois, jours, heures, minutes et secondes.",
        },
      },
      {
        "@type": "Question",
        name: "À quoi sert une calculatrice de différence entre deux dates?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Une calculatrice de différence entre deux dates est utilisée pour calculer la durée des projets, la période entre les événements, la durée du travail ou du service, la planification des événements, et le calcul de la différence d'âge entre deux personnes.",
        },
      },
    ],
  },
];

function DateCalculatorGregorian() {
  const [d1, setD1] = useState({ day: "7", month: "2", year: "2026" });
  const [d2, setD2] = useState({ day: "13", month: "2", year: "2026" });
  const [differences, setDifferences] = useState(null);
  const [error, setError] = useState(null);

  const calculateDifferences = () => {
    const date1 = new Date(parseInt(d1.year), parseInt(d1.month) - 1, parseInt(d1.day));
    const date2 = new Date(parseInt(d2.year), parseInt(d2.month) - 1, parseInt(d2.day));

    if (isNaN(date1.getTime()) || isNaN(date2.getTime())) {
      setError("Veuillez entrer des dates valides");
      setDifferences(null);
      return;
    }

    let startDate = date1 < date2 ? date1 : date2;
    let endDate = date1 < date2 ? date2 : date1;

    let years = endDate.getFullYear() - startDate.getFullYear();
    let months = endDate.getMonth() - startDate.getMonth();
    let days = endDate.getDate() - startDate.getDate();

    if (days < 0) {
      months--;
      const lastMonth = new Date(endDate.getFullYear(), endDate.getMonth(), 0);
      days += lastMonth.getDate();
    }
    if (months < 0) {
      years--;
      months += 12;
    }

    const totalMilliseconds = endDate - startDate;
    const totalSeconds = Math.floor(totalMilliseconds / 1000);
    const totalMinutes = Math.floor(totalSeconds / 60);
    const totalHours = Math.floor(totalMinutes / 60);
    const totalDays = Math.floor(totalHours / 24);
    const totalWeeks = Math.floor(totalDays / 7);
    const totalMonths = years * 12 + months;

    const yearPercentage = (totalDays / (100 * 365.25)) * 100;

    setDifferences({
      years, months, days,
      hours: 0, minutes: 0, seconds: 0,
      totalDays, totalWeeks, totalMonths, totalHours, totalMinutes, totalSeconds,
      startDate: startDate.toLocaleDateString("fr-FR", { weekday: "long", year: "numeric", month: "long", day: "numeric" }),
      endDate: endDate.toLocaleDateString("fr-FR", { weekday: "long", year: "numeric", month: "long", day: "numeric" }),
      yearPercentage: yearPercentage.toFixed(2),
      solarYears: (totalDays / 365.25).toFixed(2),
      workDays: Math.floor((totalDays / 7) * 5),
    });
    setError(null);
  };

  const renderSelectGroup = (data, setData, label) => (
    <div className="mb-4">
      <label ><strong>{label}:</strong></label>
      <div className="row">
        <div className="col-md-4">
          <select className="form-control" value={data.day} onChange={(e) => setData({ ...data, day: e.target.value })}>
            {Array.from({ length: 31 }, (_, i) => i + 1).map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div className="col-md-4">
          <select className="form-control" value={data.month} onChange={(e) => setData({ ...data, month: e.target.value })}>
            {GREGORIAN_MONTHS.map((m, i) => <option key={i + 1} value={i + 1}>{i + 1} - {m}</option>)}
          </select>
        </div>
        <div className="col-md-4">
          <select className="form-control" value={data.year} onChange={(e) => setData({ ...data, year: e.target.value })}>
            {Array.from({ length: 150 }, (_, i) => new Date().getFullYear() + 10 - i).map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>
    </div>
  );

  return (
    <div style={{ direction: "ltr" }}>
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        SCHEMA={SCHEMA}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
          <br />
          <div className="bg-success text-white text-center p-3 rounded shadow-sm">
            <h1 style={{ fontSize: "20px", margin: 0 }}>{TITLE}</h1>
          </div>

          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            <p className="mb-4">
              Cette calculatrice vous permet de calculer la différence précise entre deux dates en calendrier grégorien. Entrez les deux dates et la calculatrice affichera la différence en années, mois, jours, heures, minutes et secondes.
            </p>
            <hr />
            <MobileUnderCalAds />

            <div className="calculatorStyle mb-4 p-3 bg-light rounded border">
              <div className="p-2 mb-4 bg-success text-white text-center rounded">
                <strong>Entrez vos dates</strong>
              </div>

              {renderSelectGroup(d1, setD1, "Première date")}
              {renderSelectGroup(d2, setD2, "Deuxième date")}

              <div className="text-center mt-4">
                <button className="btn btn-success btn-lg px-5" onClick={calculateDifferences}>
                  Calculer la Différence
                </button>
              </div>

              {error && <div className="alert alert-danger mt-3">{error}</div>}

              {differences && (
                <div className="mt-5">
                  <div className="alert alert-success border-success">
                    <h3 style={{ fontSize: "18px" }}><strong>Résultat de la Différence Entre les Deux Dates:</strong></h3>
                    <div className="p-3 bg-white rounded border mt-3 text-center">
                      <strong className="text-muted">Différence:</strong>
                      <div style={{ fontSize: "24px", color: "#28a745", fontWeight: "bold" }}>
                        {differences.years} an(s) {differences.months} mois et {differences.days} jour(s)
                      </div>
                    </div>
                    <div className="mt-4">
                      <p><strong>Détails Complets:</strong></p>
                      <ul>
                        <li><strong>Date de début:</strong> {differences.startDate}</li>
                        <li><strong>Date de fin:</strong> {differences.endDate}</li>
                        <li><strong>Jours totaux:</strong> {differences.totalDays}</li>
                        <li><strong>Semaines totales:</strong> {differences.totalWeeks}</li>
                        <li><strong>Mois totaux:</strong> {differences.totalMonths}</li>
                        <li><strong>Heures totales:</strong> {differences.totalHours}</li>
                        <li><strong>Minutes totales:</strong> {differences.totalMinutes}</li>
                        <li><strong>Années solaires:</strong> {differences.solarYears}</li>
                        <li><strong>Jours de travail:</strong> {differences.workDays}</li>
                        <li><strong>Pourcentage du siècle:</strong> {differences.yearPercentage}%</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <br />
          <SecondAds />
          <br />
          <MainCalculatorsList />
          <br />
          <RelatedLinks />
          <br />
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header"><h3 style={{ fontSize: "16px" }}>Calculer la difference entre deux dates</h3></div>
              <div className="info-section-body">
                <p>Cette calculatrice convertit l'ecart entre deux dates en un resultat detaille: annees, mois, semaines, jours, heures et minutes. Elle tient compte des specificites du calendrier pour fournir un resultat fiable dans les usages personnels et professionnels.</p>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header"><h3 style={{ fontSize: "16px" }}>Exemples pratiques de calcul de duree</h3></div>
              <div className="info-section-body">
                <ul>
                  <li><strong>Grossesse:</strong> 40 semaines, soit 280 jours, a partir de la date de reference</li>
                  <li><strong>Anciennete:</strong> calcul precise d'une duree de contrat ou d'emploi</li>
                  <li><strong>Bail:</strong> verification d'une date de fin ou de preavis</li>
                  <li><strong>Delais legaux:</strong> recours, retraction, prescription en jours calendaires</li>
                </ul>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">Comprendre les résultats et les statistiques</div>
              <div className="info-section-body">
                <p><strong>Résultats de base :</strong> années / mois / jours, heures / minutes / secondes.</p>
                <p><strong>Statistiques complémentaires :</strong></p>
                <ul>
                  <li><strong>Total en jours :</strong> utile pour la comptabilité et la planification financière</li>
                  <li><strong>Total en semaines :</strong> pour le suivi de planning hebdomadaire</li>
                  <li><strong>Total en mois :</strong> pour les abonnements, périodes d'essai, facturation mensuelle</li>
                  <li><strong>Total en heures :</strong> pour les contrats de travail et les projets billables</li>
                  <li><strong>Jours ouvrés approximatifs :</strong> pour la gestion RH et les jalons de projet</li>
                </ul>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">Faits intéressants sur le calendrier</div>
              <div className="info-section-body">
                <ul>
                  <li>L'année <strong>bissextile</strong> ajoute 1 jour pour compenser les 5 h 48 min 45 s excédentaires de l'année solaire</li>
                  <li>Le calendrier grégorien a été adopté en France dès 1582 : le 4 décembre fut immédiatement suivi du 15 décembre</li>
                  <li>Certains pays n'ont adopté le calendrier grégorien qu'au XX° siècle : la Russie en 1918, la Grèce en 1923</li>
                  <li>Juillet et août ont tous les deux 31 jours — une exception due à Jules César et Auguste qui voulaient chacun un mois complet</li>
                </ul>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">Questions fréquentes</div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "16px" }}><strong>Comment est calculée la différence entre deux dates ?</strong></h3>
                <p>La calculatrice détermine le nombre total de jours entre les deux dates, puis convertit ce chiffre en années, mois et jours en tenant compte des longueurs de mois variables et des années bissextiles.</p>
                <hr />
                <h3 style={{ fontSize: "16px" }}><strong>Les années bissextiles sont-elles prises en compte ?</strong></h3>
                <p>Oui, chaque 29 février est correctement compté, ce qui garantit une précision totale même sur des périodes de plusieurs décennies.</p>
                <hr />
                <h3 style={{ fontSize: "16px" }}><strong>Puis-je calculer avec des dates historiques ?</strong></h3>
                <p>Oui, la calculatrice gère les dates historiques. Pour les dates antérieures à 1582, les résultats sont calculés en extrapolant le système grégorien proleptique.</p>
                <hr />
                <h3 style={{ fontSize: "16px" }}><strong>Mes données de dates sont-elles enregistrées ?</strong></h3>
                <p>Non, tous les calculs s'effectuent localement dans votre navigateur. Aucune date n'est envoyée ni stockée sur un serveur.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DateCalculatorGregorian;
